import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
} from '@mui/material';
import { BarcodeReader } from '../../helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function BarcodeScanner({ onDetected }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanInterval = useRef(null);
  const videoTrackRef = useRef(null);
  const { t } = useTranslation(['glossary', 'description']);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [barcodeReader, setBarcodeReader] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 3, step: 0.1 });
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [showAudioMessage, setShowAudioMessage] = useState(true);
  const audioTimeoutRef = useRef(null);

  const stopCurrentStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    async function fetchCameras() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        stream.getTracks().forEach((track) => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((d) => d.kind === 'videoinput');

        setCameras(videoInputs);

        const backCam =
          videoInputs.find(
            (cam) =>
              /back|rear|environment/i.test(cam.label) &&
              !/macro|zoom|tele|wide|ultra|ultra-wide/i.test(cam.label)
          ) ||
          videoInputs.find((cam) => /back|rear|environment/i.test(cam.label)) ||
          videoInputs.find((cam) => /main|primary/i.test(cam.label)) ||
          videoInputs[0];

        if (backCam) {
          setSelectedCameraId(backCam.deviceId);
        } else {
          setError(t('barcode.description_error', { ns: 'glossary' }));
        }
      } catch (e) {
        setError(
          t('barcode.description_error_get', { ns: 'glossary' }) + e.message
        );
      }
    }

    fetchCameras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function initBarcodeReader() {
      try {
        const { barcodeReaderError } = await BarcodeReader.setup();
        if (barcodeReaderError) {
          setError(
            'BarcodeReader no disponible: ' + barcodeReaderError.message
          );
          return;
        }

        const reader = await BarcodeReader.create();
        setBarcodeReader(reader);
      } catch (e) {
        setError(
          t('barcode.description_error_init', { ns: 'glossary' }) + e.message
        );
      }
    }

    initBarcodeReader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playBeep = async () => {
    if (!audioEnabled) return;
    try {
      const audio = new Audio('/beep.mp3');
      audio.volume = 1.0;
      audio.preload = 'auto';

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
    } catch (error) {
      console.log('Error reproduciendo beep:', error);

      try {
        const context = new (window.AudioContext ||
          window.webkitAudioContext)();
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(context.destination);

        oscillator.frequency.value = 800;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          context.currentTime + 0.1
        );

        oscillator.start(context.currentTime);
        oscillator.stop(context.currentTime + 0.1);
      } catch (fallbackError) {
        console.log('Error en fallback de beep:', fallbackError);
      }
    }
  };

  const triggerHapticFeedback = () => {
    try {
      if (navigator && typeof navigator.vibrate === 'function') {
        navigator.vibrate(100);
      }
    } catch {}
    playBeep();
  };

  const enableAudio = async () => {
    setAudioEnabled(true);

    try {
      const testAudio = new Audio('/beep.mp3');
      testAudio.volume = 0.1;
      await testAudio.play();
      testAudio.pause();
      testAudio.currentTime = 0;
    } catch (error) {
      console.log('Audio test failed:', error);
    }

    audioTimeoutRef.current = setTimeout(() => {
      setShowAudioMessage(false);
    }, 3000);
  };

  useEffect(() => {
    if (audioEnabled) return;
    const handler = () => enableAudio();
    window.addEventListener('pointerdown', handler, { once: true });
    window.addEventListener('keydown', handler, { once: true });
    window.addEventListener('touchstart', handler, { once: true });
    return () => {
      window.removeEventListener('pointerdown', handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('touchstart', handler);
    };
  }, [audioEnabled]);

  useEffect(() => {
    return () => {
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedCameraId || !videoRef.current) return;

    let cancelled = false;

    const startStream = async () => {
      stopCurrentStream();
      setScanning(false);
      setError(null);
      setLastResult(null);
      setZoom(1);
      setZoomSupported(false);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedCameraId } },
          audio: false,
        });

        if (!cancelled && videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
          const track = stream.getVideoTracks && stream.getVideoTracks()[0];
          videoTrackRef.current = track || null;
          try {
            const capabilities =
              track && track.getCapabilities ? track.getCapabilities() : null;
            if (capabilities && typeof capabilities.zoom === 'object') {
              const { min = 1, max = 3, step = 0.5 } = capabilities.zoom;
              setZoomSupported(true);
              setZoomRange({ min, max, step: step || 0.5 });

              const isIOS =
                /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.platform === 'MacIntel' &&
                  navigator.maxTouchPoints > 1);

              const defaultZoom = isIOS ? 5 : capabilities.zoom.default || min;
              const initialZoom = Math.max(min, Math.min(max, defaultZoom));
              setZoom(initialZoom);
              try {
                await track.applyConstraints({
                  advanced: [{ zoom: initialZoom }],
                });
              } catch {
                try {
                  await track.applyConstraints({ zoom: initialZoom });
                } catch {}
              }
            } else {
              setZoomSupported(false);
              setZoomRange({ min: 1, max: 3, step: 0.5 });
              setZoom(1);
            }
          } catch {
            setZoomSupported(false);
          }
          await videoRef.current.play();
          setScanning(true);

          enableAudio();
        }
      } catch (e) {
        setError(`Error al acceder a la cámara: ${e.message}`);
      }
    };

    startStream();

    return () => {
      cancelled = true;
      stopCurrentStream();
      if (scanInterval.current) {
        clearInterval(scanInterval.current);
        scanInterval.current = null;
      }
    };
  }, [selectedCameraId]);

  useEffect(() => {
    if (!scanning || !barcodeReader || !videoRef.current) return;

    scanInterval.current = setInterval(async () => {
      try {
        const result = await barcodeReader.detect(videoRef.current);
        if (result?.rawValue) {
          const code = result.rawValue;
          if (code !== lastResult) {
            setLastResult(code);
            onDetected && onDetected(code);
            triggerHapticFeedback();
          }
        }
      } catch (error) {
        if (
          !error.message?.includes(
            'Could not detect barcode from provided source'
          )
        ) {
          console.error('Barcode scanner error:', error);
        }
      }
    }, 500);

    return () => {
      if (scanInterval.current) {
        clearInterval(scanInterval.current);
        scanInterval.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning, barcodeReader, lastResult, onDetected]);

  useEffect(() => {
    if (!videoTrackRef.current) return;
    if (!zoomSupported) return;
    const track = videoTrackRef.current;
    (async () => {
      try {
        await track.applyConstraints({ advanced: [{ zoom }] });
      } catch {
        try {
          await track.applyConstraints({ zoom });
        } catch {}
      }
    })();
  }, [zoom, zoomSupported]);

  return (
    <div>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel id="select-camera">
          {t('barcode.select_cam', { ns: 'description' })}
        </InputLabel>
        <Select
          labelId="select-camera-label"
          id="select-camera"
          value={selectedCameraId || ''}
          label="Cámara"
          onChange={(e) => setSelectedCameraId(e.target.value)}
        >
          {cameras.map((cam) => (
            <MenuItem key={cam.deviceId} value={cam.deviceId}>
              {cam.label || `Cámara ${cam.deviceId}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div style={{ marginTop: 10 }}>
        <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 1 }}>
          <ZoomOutIcon
            sx={{ cursor: 'pointer' }}
            color="primary"
            onClick={() =>
              setZoom((z) =>
                Math.max(
                  zoomRange.min,
                  Number((z - (zoomRange.step || 0.5)).toFixed(2))
                )
              )
            }
          />
          <Slider
            aria-label="Volume"
            min={zoomRange.min}
            max={zoomRange.max}
            step={zoomRange.step}
            value={zoom}
            valueLabelDisplay="auto"
            marks
            onChange={(e) => setZoom(parseFloat(e.target.value))}
          />
          <ZoomInIcon
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={() =>
              setZoom((z) =>
                Math.min(
                  zoomRange.max,
                  Number((z + (zoomRange.step || 0.5)).toFixed(2))
                )
              )
            }
          />
        </Stack>
      </div>

      <div
        style={{
          width: '100%',
          marginTop: 10,
          borderRadius: 8,
          overflow: 'hidden',
          objectFit: 'cover',
        }}
        onClick={enableAudio}
      >
        <video
          ref={videoRef}
          style={{
            width: '100%',
            display: 'block',
            transform: zoomSupported ? 'none' : `scale(${zoom})`,
            transformOrigin: 'center',
          }}
          muted
          autoPlay
          playsInline
        />
      </div>
      {showAudioMessage && (
        <p
          style={{
            marginTop: 8,
            fontSize: 12,
            color: '#666',
            textAlign: 'center',
          }}
        >
          {t('touch_screen_enable_sound', { ns: 'description' })}
        </p>
      )}
    </div>
  );
}
